import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { memo, useEffect, useState, useCallback } from "react";
import { chatApi, CHATS_CHANGED_EVENT, type ChatsChangedDetail } from "../api/client";
import { authApi } from "../api/auth";
import type { Chat } from "../types";
import "./Sidebar.css";
import { useToast } from "../lib/toast";
import { useShell, useViewport } from "../lib/shell";
import { encodeModelSelection, loadLastModelSelection } from "../lib/chatModel";

/**
 * One sidebar chat row, memoized so a delta to one chat (rename, delete,
 * new chat) doesn't re-render every other row. Handlers must be
 * useCallback-stable in the parent for the memo to hold.
 */
const ChatRow = memo(function ChatRow({
  chat,
  selected,
  onToggle,
  onDelete,
}: {
  chat: Chat;
  selected: boolean;
  onToggle: (id: string) => void;
  onDelete: (chat: Chat) => void | Promise<void>;
}) {
  return (
    <div className="sidebar-chat-row">
      <input
        className="sidebar-chat-check"
        type="checkbox"
        aria-label={`Select ${chat.title}`}
        checked={selected}
        onChange={() => onToggle(chat.id)}
      />
      <NavLink
        to={`/chat/${chat.id}`}
        className={({ isActive }) =>
          "sidebar-chat" + (isActive ? " active" : "")
        }
      >
        <span className="sidebar-chat-dot" />
        <span className="sidebar-chat-title">{chat.title}</span>
      </NavLink>
      <button
        className="sidebar-chat-delete"
        type="button"
        aria-label={`Delete ${chat.title}`}
        title="Delete chat"
        onClick={() => void onDelete(chat)}
      >
        ×
      </button>
    </div>
  );
});

const NAV = [
  { to: "/", label: "Chats", icon: "💬" },
  { to: "/search", label: "Search", icon: "🔍" },
  { to: "/projects", label: "Projects", icon: "📁" },
  { to: "/memory", label: "Memory", icon: "💭" },
  { to: "/research", label: "Research", icon: "🔬" },
  { to: "/images", label: "Images", icon: "🖼️" },
  { to: "/published", label: "Published Sites", icon: "🌐" },
  { to: "/skills", label: "Skills", icon: "⚡" },
  { to: "/plugins", label: "Plugins", icon: "🧩" },
  { to: "/mcp", label: "MCP Servers", icon: "🔌" },
  { to: "/settings", label: "Settings", icon: "⚙️" },
];

/**
 * In the drawer, chat history is what people came for — ten nav rows above
 * it means scrolling past the whole feature list to reach yesterday's
 * conversation. So the drawer shows the everyday destinations up top and
 * folds the rest into an expandable "More" group at the bottom (the shape
 * ChatGPT and Claude both settled on). The desktop sidebar is unchanged:
 * it has the height for the full list.
 */
const PRIMARY_NAV = new Set(["/", "/search", "/projects"]);

export default function Sidebar() {
  const toast = useToast();
  const [chats, setChats] = useState<Chat[]>([]);
  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Below the desktop breakpoint the sidebar is an off-canvas drawer,
  // opened from MobileTopBar's ☰. The open/closed state lives in the shell
  // context (real JS state driven by a measured viewport, not a CSS-only
  // transform) so both components share one source of truth and a jsdom
  // test can assert on it — jsdom never evaluates `@media`.
  const { isCompact } = useViewport();
  const { drawerOpen, closeDrawer, sidebarCollapsed, toggleSidebarCollapsed } = useShell();
  // The icon rail is a desktop affordance only: below the desktop
  // breakpoint the sidebar is already an off-canvas drawer, and a
  // collapsed *drawer* would be an overlay of nothing but icons.
  const collapsed = !isCompact && sidebarCollapsed;

  // Picking a chat or a nav item navigates; the drawer must get out of the
  // way so the destination is actually visible.
  useEffect(() => {
    if (isCompact) closeDrawer();
    // `closeDrawer` is stable per shell state; keying off the path alone is
    // what makes this "close on navigation" rather than "close on render".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isCompact]);

  const load = useCallback(async () => {
    try {
      const list = await chatApi.list();
      setChats(list);
    } catch {
      setChats([]);
    }
  }, []);

  // One full fetch on mount; afterwards the list is kept current by
  // applying one-row deltas from api/client.ts's CHATS_CHANGED_EVENT
  // (create/rename/auto-title/delete all flow through that module).
  // The previous version refetched the whole list on every route change,
  // which meant a full-list reload + rerender each time a chat was opened.
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    authApi.me().then((u) => setIsAdmin(u.role === "admin")).catch(() => {});
  }, []);

  const navItems = isAdmin ? [...NAV, { to: "/admin/users", label: "Admin", icon: "🛡️" }] : NAV;

  useEffect(() => {
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<ChatsChangedDetail>).detail;
      setChats((current) => {
        switch (detail.type) {
          case "upsert": {
            const idx = current.findIndex((c) => c.id === detail.chat.id);
            if (idx === -1) return [detail.chat, ...current];
            const next = current.slice();
            next[idx] = { ...next[idx], ...detail.chat };
            return next;
          }
          case "title":
            return current.map((c) => (c.id === detail.id ? { ...c, title: detail.title } : c));
          case "remove":
            return current.filter((c) => c.id !== detail.id);
        }
      });
    };
    window.addEventListener(CHATS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CHATS_CHANGED_EVENT, onChanged);
  }, []);

  const newChat = async () => {
    try {
      const lastModel = loadLastModelSelection();
      // chatApi.create emits the upsert event that adds the row — no full
      // list reload needed here.
      const c = await chatApi.create({
        title: "New Chat",
        model_override: lastModel ? encodeModelSelection(lastModel) : undefined,
      });
      navigate(`/chat/${c.id}`);
    } catch (e) {
      toast.error("Failed to create chat: " + (e as Error).message);
    }
  };

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const deleteChat = useCallback(async (chat: Chat) => {
    if (!window.confirm(`Delete “${chat.title}”?`)) return;
    try {
      // chatApi.remove emits the event that drops the row from `chats`.
      await chatApi.remove(chat.id);
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(chat.id);
        return next;
      });
      if (location.pathname === `/chat/${chat.id}`) navigate("/");
    } catch (e) {
      toast.error("Failed to delete chat: " + (e as Error).message);
    }
  }, [location.pathname, navigate, toast]);

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected chat${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      // Each remove emits its own event; the list updates row-by-row.
      await Promise.all(ids.map((id) => chatApi.remove(id)));
      setSelectedIds(new Set());
      if (location.pathname.startsWith("/chat/") && ids.includes(location.pathname.slice("/chat/".length))) {
        navigate("/");
      }
    } catch (e) {
      toast.error("Failed to delete selected chats: " + (e as Error).message);
    }
  };

  const filtered = filter
    ? chats.filter((c) => c.title.toLowerCase().includes(filter.toLowerCase()))
    : chats;

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore — falling through to /login either way is correct
    }
    navigate("/login", { replace: true });
  };

  return (
    <>
      {isCompact && drawerOpen && (
        <div className="sidebar-backdrop" onClick={closeDrawer} />
      )}
      <aside
        className={`sidebar ${isCompact ? "sidebar-drawer" : ""} ${isCompact && drawerOpen ? "open" : ""} ${collapsed ? "sidebar-collapsed" : ""}`}
      >
      {/* A closed drawer is hidden with `visibility: hidden` (Sidebar.css),
          which — unlike a bare `translateX(-100%)` — also takes its links
          out of the tab order, so tabbing on a phone doesn't wander into an
          invisible menu. */}
      <div className="sidebar-brand">
        <div className="sidebar-logo">◐</div>
        {!collapsed && (
          <div className="sidebar-brand-text">
            <div className="sidebar-name">chatter</div>
          </div>
        )}
        {!isCompact && (
          <button
            className="sidebar-collapse-toggle"
            type="button"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={toggleSidebarCollapsed}
          >
            {collapsed ? "»" : "«"}
          </button>
        )}
        {isCompact && (
          <button
            className="sidebar-close"
            type="button"
            aria-label="Close menu"
            onClick={closeDrawer}
          >
            ✕
          </button>
        )}
      </div>

      <button
        className="btn btn-primary sidebar-new"
        onClick={newChat}
        title={collapsed ? "New chat" : undefined}
        aria-label={collapsed ? "New chat" : undefined}
      >
        {collapsed ? "+" : "+ New Chat"}
      </button>

      {/* Search, the chat list and its toolbar are text-only affordances —
          at 64px there is nothing legible to show, so the rail drops them
          entirely rather than clipping them. */}
      {!collapsed && (
        <>
      <input
        className="input sidebar-search"
        placeholder="Filter chats…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      <div className="sidebar-chat-toolbar">
        <span className="sidebar-chat-heading">Chats</span>
        {selectedIds.size > 0 && (
          <button className="btn btn-danger btn-sm" onClick={() => void deleteSelected()}>
            Delete {selectedIds.size}
          </button>
        )}
      </div>
      <div className="sidebar-chats">
        {filtered.length === 0 && (
          <div className="sidebar-empty dim">No chats yet</div>
        )}
        {filtered.map((c) => (
          <ChatRow
            key={c.id}
            chat={c}
            selected={selectedIds.has(c.id)}
            onToggle={toggleSelected}
            onDelete={deleteChat}
          />
        ))}
      </div>
        </>
      )}

      <nav className="sidebar-nav">
        {(isCompact ? navItems.filter((n) => PRIMARY_NAV.has(n.to)) : navItems).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            title={n.label}
            className={({ isActive }) =>
              "sidebar-nav-item" + (isActive ? " active" : "")
            }
          >
            <span className="sidebar-nav-icon">{n.icon}</span>
            {!collapsed && <span>{n.label}</span>}
          </NavLink>
        ))}
        {!isCompact && (
          <button className="sidebar-nav-item sidebar-logout" onClick={logout} title="Log out">
            <span className="sidebar-nav-icon">🚪</span>
            {!collapsed && <span>Log out</span>}
          </button>
        )}
      </nav>
      {isCompact && (
        <details className="sidebar-more">
          <summary className="sidebar-nav-item sidebar-more-summary">
            <span className="sidebar-nav-icon">⋯</span>
            <span>More</span>
          </summary>
          <nav className="sidebar-nav sidebar-nav-secondary">
            {navItems.filter((n) => !PRIMARY_NAV.has(n.to)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  "sidebar-nav-item" + (isActive ? " active" : "")
                }
              >
                <span className="sidebar-nav-icon">{n.icon}</span>
                <span>{n.label}</span>
              </NavLink>
            ))}
            <button className="sidebar-nav-item sidebar-logout" onClick={logout}>
              <span className="sidebar-nav-icon">🚪</span>
              <span>Log out</span>
            </button>
          </nav>
        </details>
      )}
      </aside>
    </>
  );
}
